import { useEffect } from 'react';

import { fetchPrzejscia } from 'api/strefy/strefy';
import { PrzejscieSortByEnum } from 'api/strefy/strefy.enum';
import { QueryKeyEnum } from 'core/global.enum';
import { QueryKeyLocalStorageEnum } from 'context/query/queryContext/QueryContext.enum';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useSavedPagination } from 'hooks/useSavedPagination/useSavedPagination';

import { PassagesList } from './PassagesList';
import { PassagesListContainerProps } from './PassagesList.types';

export const PassagesListContainer = ({
  server,
  setPassagesCount,
  isDeleteDisabled,
  deleteDisabledReason,
}: PassagesListContainerProps) => {
  const { rowsPerPage, offset, storedQueryWithoutPagination, setPaginationCount } = useSavedPagination({
    queryKey: QueryKeyLocalStorageEnum.PASSAGES,
  });

  // The app stores sorting as `nazwaTkd` / `-nazwaTkd`; the endpoint takes `sortBy` + `sortOrder`.
  const [sort] = storedQueryWithoutPagination.sort ?? [];
  const { mod } = storedQueryWithoutPagination;

  const { data: passagesResponse, isInitialLoading } = useQuery(
    [QueryKeyEnum.PRZEJSCIA, server.rcpId, storedQueryWithoutPagination, rowsPerPage, offset],
    fetchPrzejscia,
    {
      args: {
        rcpId: server.rcpId,
        limit: rowsPerPage,
        offset,
        ...(sort && {
          sortBy: sort.replace(/^-/, '') as PrzejscieSortByEnum,
          sortOrder: sort.startsWith('-') ? ('DESC' as const) : ('ASC' as const),
        }),
        ...(mod && { mod }),
      },
    },
  );

  // An effect rather than `onSuccess`: a cached response for this server has to update the counter too.
  useEffect(() => {
    if (!passagesResponse) return;

    setPassagesCount(passagesResponse.count);
    setPaginationCount(passagesResponse.count);
  }, [passagesResponse]);

  return (
    <PassagesList
      passages={passagesResponse?.data || []}
      isLoading={isInitialLoading}
      isDeleteDisabled={isDeleteDisabled}
      deleteDisabledReason={deleteDisabledReason}
    />
  );
};
